import type { QueryClient } from '@tanstack/react-query';

/**
 * Query key prefixes for everything that is derived from a client's meal logs.
 *
 * Ticking a meal on My Menu writes a MealLog on the server, and logging or
 * deleting a meal on Journal or Meals changes the same rows. Every page that
 * shows meals, points or streaks reads one of these, and with the app's
 * 5 minute staleTime a page whose cache is not invalidated keeps showing the
 * old numbers until a reload. Invalidating by prefix covers every date and
 * filter variant, and any new query that reuses a prefix.
 */
export const MEAL_LOG_QUERY_PREFIXES = [
    ['my-meal-logs'],
    ['meal-log'],
    ['my-daily-meals'],
    ['my-daily-points-summary'],
    // The meal streak.
    ['my-meal-stats'],
    // Journal stats sum Total Points from MealLog.
    ['my-journal-stats'],
    // Journal entries carry their meal logs and derived points.
    ['my-journal-entries'],
] as const;

/** Query key prefixes for the menu views, which carry each meal's ticked state. */
export const MENU_QUERY_PREFIXES = [['my-menu'], ['my-plan']] as const;

/**
 * Mark every meal-log-derived query stale. Mounted queries refetch at once;
 * the rest refetch the next time a page mounts them.
 */
export function invalidateMealLogData(queryClient: QueryClient): Promise<void> {
    return Promise.all(
        MEAL_LOG_QUERY_PREFIXES.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey: [...queryKey] }),
        ),
    ).then(() => undefined);
}

/** Mark the menu views stale (My Menu, the dashboard's plan card). */
export function invalidateMenuData(queryClient: QueryClient): Promise<void> {
    return Promise.all(
        MENU_QUERY_PREFIXES.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey: [...queryKey] }),
        ),
    ).then(() => undefined);
}

/**
 * For any mutation that creates, changes or deletes a meal log or changes a
 * menu meal's ticked state: refresh the menus and everything built on logs.
 */
export function invalidateMealData(queryClient: QueryClient): Promise<void> {
    return Promise.all([invalidateMealLogData(queryClient), invalidateMenuData(queryClient)]).then(
        () => undefined,
    );
}
